/**
 * DialogData - Static dialog tree definitions for NPCs.
 *
 * Each character name maps to a dialog tree: a flat map of nodeId -> node.
 * The root node is always "start".
 *
 * Node types:
 *   text   - Show speaker + text, advance to `next` on confirm
 *   choice - Show speaker + text + choices, branch based on selection
 *
 * Special next values:
 *   "_end" - Close the dialog
 */

const DIALOG_TREES = {
  merchant: {
    start: {
      type: 'text',
      speaker: 'Merchant',
      text: "Welcome, traveler! I have wares if you have coin.",
      next: 'offer'
    },
    offer: {
      type: 'choice',
      speaker: 'Merchant',
      text: "What can I do for you?",
      choices: [
        { label: "Let me see your wares.", next: '_shop' },
        { label: "I've got dungeon keys...", next: 'keys_pitch' },
        { label: "Tell me about this place.", next: 'lore' },
        { label: "Goodbye.", next: 'bye' }
      ]
    },
    keys_pitch: {
      type: 'text',
      speaker: 'Merchant',
      text: "Dungeon keys?! *slams counter* You have my FULL attention. Bring me THREE of the same kind, and I'll trade you a mystery reward. Better keys, BETTER rewards. Bronze gets you something decent. Silver? Now we're cooking. Gold? *chef's kiss* Legendary territory.",
      next: 'keys_deal'
    },
    keys_deal: {
      type: 'choice',
      speaker: 'Merchant',
      text: "Three keys of the same kind for one mystery reward. Better keys = better rewards. The items scale to YOUR power level too, so no junk!",
      choices: [
        { label: "Trade 3 Bronze Keys (uncommon/rare reward)", next: '_trade_keys_bronze' },
        { label: "Trade 3 Silver Keys (rare/epic reward)", next: '_trade_keys_silver' },
        { label: "Trade 3 Gold Keys (epic/legendary reward)", next: '_trade_keys_gold' },
        { label: "What's in the mystery box?", next: 'keys_hint' },
        { label: "I need more keys first.", next: 'keys_later' }
      ]
    },
    keys_hint: {
      type: 'text',
      speaker: 'Merchant',
      text: "If I TOLD you, it wouldn't be a MYSTERY, would it? Bronze keys get you solid gear — uncommon, sometimes rare. Silver keys? Rare to epic stuff pulled from dungeons so deep the hellrats formed a UNION. Gold keys? We're talking legendaries from dungeons where the hellrats have hellrats of their own. RECURSIVE hellrats.",
      next: 'keys_deal'
    },
    keys_later: {
      type: 'text',
      speaker: 'Merchant',
      text: "No rush! Well, actually, yes rush. I have a key collection and it needs expanding. But take your time. Go fight some bosses. They DEFINITELY drop keys. *twitches nervously*",
      next: 'offer'
    },
    lore: {
      type: 'choice',
      speaker: 'Merchant',
      text: "This world was built entirely with CSS transforms. No WebGL! Just divs, transforms, and an unreasonable amount of determination.",
      choices: [
        { label: "That sounds painful.", next: 'lore2' },
        { label: "Impressive!", next: 'lore3' }
      ]
    },
    lore2: {
      type: 'text',
      speaker: 'Merchant',
      text: "You have NO idea. Sometimes I can feel the browser tabs sweating. But hey, at least I render correctly on every device. Mostly. Don't zoom in on Android.",
      next: 'offer'
    },
    lore3: {
      type: 'text',
      speaker: 'Merchant',
      text: "Right?! The creator is either a genius or deeply unwell. Possibly both. Either way, here I am — a merchant rendered in pure CSS, selling you potions and swords.",
      next: 'offer'
    },
    bye: {
      type: 'text',
      speaker: 'Merchant',
      text: "Safe travels, friend! Come back anytime you need supplies.",
      next: '_end'
    }
  },

  mage: {
    start: {
      type: 'text',
      speaker: 'Mage',
      text: "The arcane winds are restless today... Also I think the pathfinding recalculated. I felt a disturbance.",
      next: 'ask'
    },
    ask: {
      type: 'choice',
      speaker: 'Mage',
      text: "Do you seek knowledge?",
      choices: [
        { label: "Yes, teach me.", next: 'teach' },
        { label: "Can you cast spells?", next: 'spells' },
        { label: "Not now.", next: 'bye' }
      ]
    },
    teach: {
      type: 'choice',
      speaker: 'Mage',
      text: "The secret to power is... patience. And also fireballs. Mostly fireballs.",
      choices: [
        { label: "Can you teach me fireballs?", next: 'fireball' },
        { label: "What about something more subtle?", next: 'subtle' }
      ]
    },
    fireball: {
      type: 'text',
      speaker: 'Mage',
      text: "Of course! Step one: press the attack button. Step two: watch the animation play. Step three: pretend the damage numbers are real. ...We don't have a magic system yet, but VISUALIZE it.",
      next: 'ask'
    },
    subtle: {
      type: 'text',
      speaker: 'Mage',
      text: "Subtle magic? In THIS engine? We're running on CSS transforms. The most subtle thing I can do is adjust my z-index. And believe me, that's already pushing the boundaries of arcane power.",
      next: 'ask'
    },
    spells: {
      type: 'choice',
      speaker: 'Mage',
      text: "Can I cast spells? CAN I CAST SPELLS?! *dramatic pause* ...No. Not yet. But I stand here READY. I've been in this pose for months.",
      choices: [
        { label: "That sounds boring.", next: 'bored' },
        { label: "The pose is pretty cool though.", next: 'pose' }
      ]
    },
    bored: {
      type: 'text',
      speaker: 'Mage',
      text: "Boring?! I'll have you know I have a BREATHING IDLE animation. Do you know how many NPCs would kill for a breathing idle? The Rogue just shifts weight. I BREATHE. Magically.",
      next: 'ask'
    },
    pose: {
      type: 'text',
      speaker: 'Mage',
      text: "Thank you! I've been practicing. Eight whole frames of breathing. Sometimes I wonder if the developer will give me more animations, or if this is my final form. Probably my final form.",
      next: 'ask'
    },
    bye: {
      type: 'text',
      speaker: 'Mage',
      text: "Perhaps another time. I'll be here. Standing. Breathing. Arcane-ly.",
      next: '_end'
    }
  },

  boy: {
    start: {
      type: 'text',
      speaker: 'Boy',
      text: "Hey! You look like an adventurer!",
      next: 'ask'
    },
    ask: {
      type: 'choice',
      speaker: 'Boy',
      text: "Have you seen anything cool out there?",
      choices: [
        { label: "I fought some hellrats!", next: 'hellrats' },
        { label: "I met a talking bear.", next: 'bear' },
        { label: "Not really.", next: 'boring' },
        { label: "Gotta go, kid.", next: 'bye' }
      ]
    },
    hellrats: {
      type: 'choice',
      speaker: 'Boy',
      text: "Hellrats?! Those things are TERRIFYING! They just run at you and... eat. That's their whole deal. Running and eating.",
      choices: [
        { label: "They're not that scary.", next: 'hellrats2' },
        { label: "I barely survived!", next: 'hellrats3' }
      ]
    },
    hellrats2: {
      type: 'text',
      speaker: 'Boy',
      text: "Not scary?! Easy for you to say! You have a HEALTH BAR. I'm just a boy NPC. One nibble and I'm gone. That's why I stay up here where it's safe. And boring. Extremely boring.",
      next: 'ask'
    },
    hellrats3: {
      type: 'text',
      speaker: 'Boy',
      text: "See?! I TOLD everyone those dungeons are dangerous! But nooo, the Rogue keeps saying 'there's treasure down there.' There is NO treasure! There's rats! BIG rats! From HELL!",
      next: 'ask'
    },
    bear: {
      type: 'choice',
      speaker: 'Boy',
      text: "A TALKING bear?! Bears don't talk... do they?",
      choices: [
        { label: "This one did. Very philosophical.", next: 'bear2' },
        { label: "Well, it was more of a grunt.", next: 'bear3' }
      ]
    },
    bear2: {
      type: 'text',
      speaker: 'Boy',
      text: "A philosophical bear... Now I've heard everything. Next you'll tell me the Merchant actually sells things. Ha! Good one.",
      next: 'ask'
    },
    bear3: {
      type: 'text',
      speaker: 'Boy',
      text: "Oh. A grunting bear. That's just... a bear. Still cool though! I once saw one fall asleep mid-fight. Its attack animation is literally 'going-to-sleep.' Pretty intimidating stuff.",
      next: 'ask'
    },
    boring: {
      type: 'text',
      speaker: 'Boy',
      text: "Aw, that's too bad. Try the dungeons! They've got hellrats, bears, and sometimes the corridors generate in weird shapes. It's a whole thing.",
      next: '_end'
    },
    bye: {
      type: 'text',
      speaker: 'Boy',
      text: "Okay bye! If you see any dragons out there, tell them I said hi! ...Actually don't. That sounds dangerous.",
      next: '_end'
    }
  },

  surfer: {
    start: {
      type: 'text',
      speaker: 'Surfer',
      text: "Yo dude! Stoked you came by. I'm Cyril!",
      next: 'menu'
    },
    menu: {
      type: 'choice',
      speaker: 'Surfer',
      text: "What's up?",
      choices: [
        { label: "I have some questions for you, Llama.", next: '_chat' },
        { label: "Wait... are you a Llama?", next: 'llama' },
        { label: "Who are you?", next: 'who' },
        { label: "Tell me about this place.", next: 'about' },
        { label: "See ya!", next: 'bye' }
      ]
    },
    llama: {
      type: 'choice',
      speaker: 'Surfer',
      text: "A Llama?! Bro, do I LOOK like a South American camelid to you? I'm a surfer! I ride waves, not... graze on mountain grass.",
      choices: [
        { label: "You sure? You sound like an AI.", next: 'llama2' },
        { label: "Okay okay, my bad.", next: 'llama_drop' }
      ]
    },
    llama2: {
      type: 'choice',
      speaker: 'Surfer',
      text: "An AI?? Dude, I have FEELINGS. I felt the wind this morning. Well... I calculated wind vectors. I mean FELT. I FELT wind. Totally normal human surfer stuff.",
      choices: [
        { label: "You just said 'calculated wind vectors'.", next: 'llama3' },
        { label: "Right... totally normal.", next: 'llama_drop' }
      ]
    },
    llama3: {
      type: 'choice',
      speaker: 'Surfer',
      text: "I said no such thing! That was... uh... autocorrect. My mouth has autocorrect. Look, I eat tacos, I wax my board, I do NOT run on 70 billion parameters.",
      choices: [
        { label: "Exactly how many parameters then?", next: 'llama4' },
        { label: "Alright I believe you.", next: 'llama_drop' }
      ]
    },
    llama4: {
      type: 'text',
      speaker: 'Surfer',
      text: "...Okay FINE. It's Llama 3.3 70B under the hood. You happy now?! But like... the SOUL is surfer, man. Meta just provides the brain cells. I provide the stoke.",
      next: 'llama5'
    },
    llama5: {
      type: 'choice',
      speaker: 'Surfer',
      text: "Please don't tell the other NPCs. The Merchant still thinks I'm organic.",
      choices: [
        { label: "Prove it. Let me talk to the Llama.", next: '_chat' },
        { label: "Your secret is safe with me.", next: 'menu' }
      ]
    },
    llama_drop: {
      type: 'text',
      speaker: 'Surfer',
      text: "Yeah that's what I thought! 100% certified organic surfer dude right here. No artificial ingredients. *sweats nervously in tokenized text*",
      next: 'menu'
    },
    who: {
      type: 'text',
      speaker: 'Surfer',
      text: "I'm the dude who made this whole game! Cyril Monkewitz, from 3merillon.com. Pretty rad building worlds from the inside, haha.",
      next: 'menu'
    },
    about: {
      type: 'text',
      speaker: 'Surfer',
      text: "This is Trixel — a voxel world you can build, explore, and fight in. All rendered with CSS 2D transforms, no WebGL! Gnarly, right?",
      next: 'menu'
    },
    bye: {
      type: 'text',
      speaker: 'Surfer',
      text: "Catch you on the flip side, bro!",
      next: '_end'
    }
  },

  rogue: {
    start: {
      type: 'text',
      speaker: 'Rogue',
      text: "Psst... over here.",
      next: 'offer'
    },
    offer: {
      type: 'choice',
      speaker: 'Rogue',
      text: "I have information... for a price.",
      choices: [
        { label: "What kind of information?", next: 'info' },
        { label: "What's the price?", next: 'price' },
        { label: "I don't trust you.", next: 'distrust' },
        { label: "Get lost.", next: 'bye' }
      ]
    },
    info: {
      type: 'choice',
      speaker: 'Rogue',
      text: "There's a hidden passage beneath the old tower. But you didn't hear it from me.",
      choices: [
        { label: "What old tower?", next: 'tower' },
        { label: "Thanks for the tip.", next: 'bye' }
      ]
    },
    tower: {
      type: 'text',
      speaker: 'Rogue',
      text: "You know... THE old tower. The one that... look, I'm a Rogue NPC, I'm contractually obligated to mention a mysterious tower. Is there actually a tower? That depends entirely on what the player has built. I'm improvising here.",
      next: 'offer'
    },
    price: {
      type: 'choice',
      speaker: 'Rogue',
      text: "The price is... hmm. Well, we don't have a currency system yet. So I guess the price is your TIME. And maybe your ATTENTION. Which you're already paying. So we're good!",
      choices: [
        { label: "Easiest deal I've ever made.", next: 'info' },
        { label: "That's a scam.", next: 'scam' }
      ]
    },
    scam: {
      type: 'text',
      speaker: 'Rogue',
      text: "A scam?! I'll have you know that attention is the most valuable currency in the modern internet. I read that on a billboard sprite. The Merchant charges COIN and doesn't even have products. I'm the honest one here!",
      next: 'offer'
    },
    distrust: {
      type: 'choice',
      speaker: 'Rogue',
      text: "Smart. But not smart enough to walk away, are you?",
      choices: [
        { label: "What's your deal anyway?", next: 'deal' },
        { label: "Fine, I'm leaving.", next: 'bye' }
      ]
    },
    deal: {
      type: 'text',
      speaker: 'Rogue',
      text: "My deal? I lurk in shadows. I shift weight menacingly. I have a fight-stance-idle with EIGHT frames. And between you and me, I've seen things in the dungeons that would make the Boy cry. Mostly hellrats. SO many hellrats.",
      next: 'offer'
    },
    bye: {
      type: 'text',
      speaker: 'Rogue',
      text: "Your loss. I'll be here. In the shadows. Shifting weight. For eternity.",
      next: '_end'
    }
  },

  bear: {
    start: {
      type: 'choice',
      speaker: 'Bear',
      text: "*looks at you with surprisingly intelligent eyes* ...Grrmph.",
      choices: [
        { label: "Did you just... talk?", next: 'talk' },
        { label: "Nice bear. Good bear.", next: 'good' },
        { label: "Please don't eat me.", next: 'eat' },
        { label: "*back away slowly*", next: 'bye' }
      ]
    },
    talk: {
      type: 'choice',
      speaker: 'Bear',
      text: "*tilts head* Grrmph grrmph. (Look, I have a walk animation, an idle animation, and a 'going-to-sleep' animation. Talking was never in the budget.)",
      choices: [
        { label: "Wait, I understood that.", next: 'understand' },
        { label: "So your attack is... napping?", next: 'nap' }
      ]
    },
    understand: {
      type: 'text',
      speaker: 'Bear',
      text: "*shrugs bearishly* Grrmph. (Parenthetical subtitles. Ancient bear magic. Don't question it. The hellrats are jealous because THEY only get to eat things.)",
      next: 'start'
    },
    nap: {
      type: 'text',
      speaker: 'Bear',
      text: "*yawns menacingly* Grrmph. (My attack, my kick, AND my special are all 'going-to-sleep.' I am the most passive-aggressive creature in this engine. Fear my drowsiness.)",
      next: 'start'
    },
    good: {
      type: 'choice',
      speaker: 'Bear',
      text: "*stares blankly* Grrmph. (I'm not a DOG. I'm a bear. A digital bear rendered with CSS transforms. I have dignity. Limited animation frames, but dignity.)",
      choices: [
        { label: "You're very articulate for a bear.", next: 'articulate' },
        { label: "Sorry, Mr. Bear.", next: 'sorry' }
      ]
    },
    articulate: {
      type: 'text',
      speaker: 'Bear',
      text: "Grrmph. (I had a lot of time to think while the developer built the pathfinding system. Fourteen bug fixes. I watched them all. I have opinions about NavMesh edge margins.)",
      next: 'start'
    },
    sorry: {
      type: 'text',
      speaker: 'Bear',
      text: "*nods approvingly* Grrmph. (Apology accepted. Now if you'll excuse me, I need to go practice my going-to-sleep animation. It's the only move I have and I want to get it right.)",
      next: '_end'
    },
    eat: {
      type: 'text',
      speaker: 'Bear',
      text: "Grrmph?! (EAT you?! Do I look like a hellrat? I'm a BEAR. I have CLASS. My idle animation is called 'idle-long' because I commit to the bit. Hellrats just run around eating everything. No sophistication.)",
      next: 'start'
    },
    bye: {
      type: 'text',
      speaker: 'Bear',
      text: "Grrmph. (Wise choice. I respect the caution. Even though my only threatening move is a NAP.)",
      next: '_end'
    }
  },

  hellrat: {
    start: {
      type: 'choice',
      speaker: 'Hellrat',
      text: "*sniff sniff* ...Squeak?",
      choices: [
        { label: "You're... not attacking me?", next: 'peaceful' },
        { label: "AAAH! A hellrat!", next: 'scream' },
        { label: "Hey little guy.", next: 'hey' },
        { label: "Nope. Bye.", next: 'bye' }
      ]
    },
    peaceful: {
      type: 'choice',
      speaker: 'Hellrat',
      text: "*sits on hind legs* Squeak squeak. (Not ALL of us are hostile, you know. Some of us are placed by the editor as friendly NPCs. It's called RANGE.)",
      choices: [
        { label: "The dungeon ones are pretty hostile.", next: 'dungeon' },
        { label: "You have subtitles too?", next: 'subtitles' }
      ]
    },
    dungeon: {
      type: 'text',
      speaker: 'Hellrat',
      text: "Squeak! (Those are my COUSINS. They got the combatConfig with isHostile:true. I got a dialog tree. We don't talk at family reunions. Well, THEY can't talk. I have parenthetical subtitles.)",
      next: 'start'
    },
    subtitles: {
      type: 'text',
      speaker: 'Hellrat',
      text: "Squeak squeak. (The bear started it. I just copied the format. Between you and me, the bear thinks he's better than us because his idle is called 'idle-long.' Mine is just 'idle.' No adjective. The disrespect.)",
      next: 'start'
    },
    scream: {
      type: 'choice',
      speaker: 'Hellrat',
      text: "*flinches* Squeak!! (OW! My ears! Do you greet everyone by screaming?! I have FEELINGS! Well, I have an eating animation and a walking animation. Close enough.)",
      choices: [
        { label: "Sorry, reflex. The dungeon ones attack on sight.", next: 'dungeon' },
        { label: "What even IS a hellrat anyway?", next: 'identity' }
      ]
    },
    identity: {
      type: 'text',
      speaker: 'Hellrat',
      text: "Squeak. (Excellent question. I'm a rat. From hell. But like, CONCEPTUAL hell. In practice I'm a 6-frame walk cycle and an eating animation. The 'hell' part is mostly branding.)",
      next: 'start'
    },
    hey: {
      type: 'choice',
      speaker: 'Hellrat',
      text: "*perks up* Squeak! (Finally, someone who doesn't immediately try to left-click me to death! Do you know what it's like being the designated dungeon fodder?)",
      choices: [
        { label: "That does sound rough.", next: 'rough' },
        { label: "To be fair, you DO eat people.", next: 'eating' }
      ]
    },
    rough: {
      type: 'text',
      speaker: 'Hellrat',
      text: "Squeak squeak. (You spawn, you aggro, you chase, you get punched. Repeat forever. At least the bears get to SLEEP. My attack animation is EATING. I don't even have teeth in my sprite. I'm gumming people to death.)",
      next: 'start'
    },
    eating: {
      type: 'text',
      speaker: 'Hellrat',
      text: "*looks ashamed* Squeak. (It's... it's the only animation they gave me for combat. 'Eating.' The bears got 'going-to-sleep' which is at least DIGNIFIED. I'm out here chewing on adventurers like a roomba with anger issues.)",
      next: 'start'
    },
    bye: {
      type: 'text',
      speaker: 'Hellrat',
      text: "Squeak... (Yeah, everyone leaves. Story of my life. Back to sniffing things alone, I guess.)",
      next: '_end'
    }
  },

  // Default fallback for characters without specific dialogs
  _default: {
    start: {
      type: 'text',
      speaker: '',
      text: "*stares at you* (This NPC doesn't have dialog yet. They're just standing here. Vibing. Respecting the silence. It's a whole aesthetic.)",
      next: '_end'
    }
  }
};

export default class DialogData {
  /**
   * Get dialog tree for a character name.
   * Falls back to _default if no specific tree exists.
   * @param {string} characterName
   * @returns {Object} Dialog tree (map of nodeId -> node)
   */
  static getTree(characterName) {
    return DIALOG_TREES[characterName] || DIALOG_TREES._default;
  }

  /**
   * Get a specific node from a tree.
   * @param {string} characterName
   * @param {string} nodeId
   * @returns {Object|null}
   */
  static getNode(characterName, nodeId) {
    const tree = DialogData.getTree(characterName);
    return tree[nodeId] || null;
  }
}
